import { getMinSpanYAxis } from 'common/utils/chartYAxis'
import { format } from 'date-fns'
import { lazy, Suspense, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Skeleton, Typography, useTheme } from '@mui/material'

import { classifyZone, getSensorMeta, toThreshold } from '../api/selector'
import { IotSensorHistoryResponse, IotThresholdRange, SensorZone } from '../api/types'

const ReactECharts = lazy(() => import('echarts-for-react'))

// Smallest Y span for sensor charts, so a near-flat reading isn't stretched into big swings.
const SENSOR_MIN_SPAN = 0.5

interface SensorChartProps {
  sensor: string
  history: IotSensorHistoryResponse
  threshold: IotThresholdRange | undefined
  current: number | null | undefined
  unit?: string
  height?: number
  /** Selected time range (ms). The x axis spans it even where there's no data. */
  rangeStart?: number
  rangeEnd?: number
}

export default function SensorChart({
  sensor,
  history,
  threshold,
  current,
  unit,
  height = 240,
  rangeStart,
  rangeEnd,
}: SensorChartProps) {
  const { t } = useTranslation('iot')
  const theme = useTheme()
  const meta = getSensorMeta(sensor)

  const isMinuteInterval = unit === 'minute'
  const isDayOrWeek = unit === 'day' || unit === 'week'

  const { option } = useMemo(() => {
    const histories = history.histories
    if (histories.length === 0) {
      const now = Date.now()
      const emptyOption = {
        animation: false,
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: { text: t('sensor.no_data'), fill: theme.palette.text.secondary, fontSize: 13 },
        },
        grid: { top: 16, right: 16, bottom: 32, left: 48, containLabel: false },
        xAxis: {
          type: 'time',
          min: now - 900_000,
          max: now,
          axisLabel: {
            formatter: (v: number) => format(new Date(v), 'HH:mm'),
            fontSize: 10,
            color: theme.palette.text.secondary,
          },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 10,
          axisLabel: { fontSize: 10, color: theme.palette.text.secondary },
          splitLine: { lineStyle: { color: theme.palette.divider } },
        },
        series: [],
      }
      return { option: emptyOption, hasData: false }
    }

    // No threshold and no current value (e.g. %Sat before the device has a
    // threshold): toThreshold would collapse every band to 0 and paint the
    // whole line critical, so draw it as normal with no zone lines — same as
    // mobile.
    const hasZones = threshold != null || current != null
    const th = hasZones
      ? toThreshold(threshold, current)
      : { critLow: -Infinity, warnLow: -Infinity, warnHigh: Infinity, critHigh: Infinity }

    const ZONE_COLOR: Record<SensorZone, string> = {
      normal: theme.palette.primary.main,
      warning: theme.palette.warning.main,
      critical: theme.palette.error.main,
    }

    const BOUNDARY_THRESHOLDS = hasZones ? [th.critLow, th.warnLow, th.warnHigh, th.critHigh] : []
    const isTemperature = sensor === 'temperature' || sensor === 'temp'
    const CRITICAL_THRESHOLDS = isTemperature || !hasZones ? [] : [th.critLow, th.critHigh]

    const buildSegments = (points: typeof histories) => {
      const pieces: { min: number; max: number; color: string }[] = []
      const sorted = [...BOUNDARY_THRESHOLDS].sort((a, b) => a - b)
      const allValues = points.map((p) => p.value)
      const dataMin = Math.min(...allValues)
      const dataMax = Math.max(...allValues)
      const lo = Math.min(dataMin, ...sorted) - 1
      const hi = Math.max(dataMax, ...sorted) + 1
      const thresholds = [lo, ...sorted, hi]

      for (let i = 0; i < thresholds.length - 1; i++) {
        const mid = (thresholds[i] + thresholds[i + 1]) / 2
        const zone = classifyZone(mid, th)
        pieces.push({ min: thresholds[i], max: thresholds[i + 1], color: ZONE_COLOR[zone] })
      }
      return pieces
    }

    const timestamps = histories.map((p) => new Date(p.timestamp).getTime())
    // Pin the axis to the selected range, not the data extent: otherwise 1/3/7
    // days look identical when the device only has the last day (DOF-1348).
    const hasRange = rangeStart != null && rangeEnd != null && rangeEnd > rangeStart
    const axisStart = hasRange ? rangeStart : timestamps[0]
    const axisEnd = hasRange ? rangeEnd : timestamps[timestamps.length - 1]
    const spansMultipleDays =
      axisEnd > axisStart && new Date(axisStart).toDateString() !== new Date(axisEnd).toDateString()

    const parseIntervalMs = (iv: string | null): number => {
      if (!iv) return 0
      const match = iv.match(/^(\d+)(m|h|d|wk)$/)
      if (!match) return 0
      const n = parseInt(match[1], 10)
      switch (match[2]) {
        case 'm':
          return n * 60_000
        case 'h':
          return n * 3_600_000
        case 'd':
          return n * 86_400_000
        case 'wk':
          return n * 604_800_000
        default:
          return 0
      }
    }
    const expectedInterval = parseIntervalMs(history.interval)
    const gapThreshold = expectedInterval * 1.5

    const points: { value: [number, number | null]; min?: number | null; max?: number | null }[] =
      []
    for (let i = 0; i < histories.length; i++) {
      if (i > 0 && gapThreshold > 0 && timestamps[i] - timestamps[i - 1] > gapThreshold) {
        points.push({ value: [timestamps[i - 1] + 1, null] })
      }
      points.push({
        value: [timestamps[i], histories[i].value],
        min: histories[i].min,
        max: histories[i].max,
      })
    }

    // Min–max band drawn as a stack: an invisible `min` line plus a filled
    // `max - min` on top, so only the band itself is painted. (A white fill
    // under `min` used to hide the grid lines and anything behind it.)
    // Time axes stack by data index, so both arrays must stay index-aligned.
    const bandBase: [number, number | null][] = []
    const bandSpan: [number, number | null][] = []
    for (let i = 0; i < histories.length; i++) {
      if (i > 0 && gapThreshold > 0 && timestamps[i] - timestamps[i - 1] > gapThreshold) {
        bandBase.push([timestamps[i - 1] + 1, null])
        bandSpan.push([timestamps[i - 1] + 1, null])
      }
      const { min, max } = histories[i]
      if (min === null || max === null) continue
      bandBase.push([timestamps[i], min])
      bandSpan.push([timestamps[i], max - min])
    }
    const hasBand = bandBase.length > 0

    const bandValues = histories.flatMap((p) =>
      [p.value, p.min, p.max].filter((v): v is number => v !== null && v !== undefined)
    )
    const dynamicYAxis = getMinSpanYAxis(bandValues, {
      minSpan: SENSOR_MIN_SPAN,
      pointCount: histories.length,
    })

    const markLineData = CRITICAL_THRESHOLDS.filter(
      (v) => v !== undefined && (!dynamicYAxis || (v >= dynamicYAxis.min && v <= dynamicYAxis.max))
    ).map((v) => ({
      yAxis: v,
    }))

    const segments = buildSegments(histories)

    // Vertical "ปรับเทียบ" markers at each calibration inside the visible
    // window. [] / null / missing = nothing to draw (DOF-1324).
    const calibrationLines = (history.calibrations ?? [])
      .map((c) => new Date(c.timestamp).getTime())
      .filter((ts) => !isNaN(ts) && ts >= axisStart && ts <= axisEnd)
      .map((ts) => ({
        xAxis: ts,
        lineStyle: { color: theme.palette.warning.main, type: 'dashed', width: 1.5 },
        label: {
          show: true,
          position: 'end',
          formatter: t('chart.calibration'),
          color: theme.palette.common.white,
          backgroundColor: theme.palette.warning.main,
          borderRadius: 4,
          padding: [3, 8],
          fontSize: 10,
          fontWeight: 600,
        },
      }))

    const option = {
      animation: false,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { type: 'dashed', color: '#999' } },
        backgroundColor: '#fff',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        textStyle: { color: '#333', fontSize: 12 },
        extraCssText: 'border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,0.12);padding:10px 14px;',
        formatter: (params: any[]) => {
          if (!params.length) return ''
          const p = params[0]
          const ts = p.value[0]
          const avg: number | null = p.value[1] ?? null
          const min: number | null = p.data?.min ?? null
          const max: number | null = p.data?.max ?? null
          const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : '-')
          const sensorUnit = meta.unit
          const unitSpan = sensorUnit
            ? `<span style="color:#aaa;font-size:10px"> ${sensorUnit}</span>`
            : ''
          const zoneColor = (v: number | null) => {
            if (v === null) return '#333'
            const zone = classifyZone(v, th)
            if (zone === 'critical') return theme.palette.error.main
            if (zone === 'warning') return theme.palette.warning.main
            return '#333'
          }
          const rows = [
            `<div style="font-weight:600;margin-bottom:6px">${format(
              new Date(ts),
              'dd/MM/yyyy HH:mm'
            )}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#888">ค่าเฉลี่ย</span><span style="font-weight:500">${fmt(
              avg
            )}${unitSpan}</span></div>`,
          ]
          if (!isMinuteInterval) {
            rows.splice(
              1,
              0,
              `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#888">ค่าสูงสุด</span><span style="color:${zoneColor(
                max
              )};font-weight:500">${fmt(max)}${unitSpan}</span></div>`
            )
            rows.push(
              `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#888">ค่าต่ำสุด</span><span style="color:${zoneColor(
                min
              )};font-weight:500">${fmt(min)}${unitSpan}</span></div>`
            )
          }
          return rows.join('')
        },
      },
      // Extra top room for the "ปรับเทียบ" badges above the plot.
      grid: {
        top: calibrationLines.length > 0 ? 36 : 16,
        right: 16,
        bottom: 32,
        left: 48,
        containLabel: false,
      },
      xAxis: {
        type: 'time',
        ...(hasRange ? { min: rangeStart, max: rangeEnd } : {}),
        // Date-only labels need ticks at least a day apart, or the same date repeats.
        ...(isDayOrWeek ? { minInterval: 86_400_000 } : {}),
        axisLabel: {
          formatter: (v: number) =>
            format(
              new Date(v),
              isDayOrWeek ? 'dd/MM' : spansMultipleDays ? 'dd/MM HH:mm' : 'HH:mm'
            ),
          fontSize: 10,
          color: theme.palette.text.secondary,
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        ...(dynamicYAxis
          ? { min: dynamicYAxis.min, max: dynamicYAxis.max, interval: dynamicYAxis.interval }
          : {}),
        axisLabel: { fontSize: 10, color: theme.palette.text.secondary },
        splitLine: { lineStyle: { color: theme.palette.divider } },
      },
      // The band's zone gradient is laid out along the y axis (piece bounds →
      // pixels), so it colours the band by its real height despite stacking.
      visualMap: {
        show: false,
        dimension: 1,
        seriesIndex: hasBand ? [0, 2] : [0],
        pieces: segments.map((s) => ({ gte: s.min, lt: s.max, color: s.color })),
      },
      series: [
        {
          type: 'line',
          data: points,
          smooth: true,
          connectNulls: false,
          symbol: 'circle',
          symbolSize: 8,
          showSymbol: false,
          lineStyle: { width: 2 },
          emphasis: { scale: false },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: theme.palette.divider, type: 'dashed', width: 2 },
            label: { show: false },
            data: [...markLineData, ...calibrationLines],
          },
        },
        ...(hasBand
          ? [
              {
                type: 'line',
                data: bandBase,
                stack: 'band',
                smooth: true,
                symbol: 'none',
                silent: true,
                lineStyle: { width: 0, opacity: 0 },
                tooltip: { show: false },
              },
              {
                type: 'line',
                data: bandSpan,
                stack: 'band',
                smooth: true,
                symbol: 'none',
                silent: true,
                lineStyle: { width: 0, opacity: 0 },
                areaStyle: { opacity: 0.12 },
                tooltip: { show: false },
              },
            ]
          : []),
      ],
    }

    return { option, hasData: true }
  }, [history, threshold, current, meta, theme, isMinuteInterval, rangeStart, rangeEnd])

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ml: 1, color: 'primary.main' }}>
        {t(meta.chartLabel ?? meta.label)}
      </Typography>
      {option ? (
        <Suspense fallback={<Skeleton variant="rounded" sx={{ height, width: '100%' }} />}>
          <ReactECharts option={option} style={{ height, width: '100%' }} notMerge lazyUpdate />
        </Suspense>
      ) : null}
    </Box>
  )
}
